import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import BootcampOverviewTab from './BootcampOverviewTab';
import BootcampIndividualStatusTab from './BootcampIndividualStatusTab';
import BootcampContextualFilters from './BootcampContextualFilters';
import LeadershipExportDrawer from './export/LeadershipExportDrawer';
import { SOLUTION_DEMO_EXPORT_CONFIG } from './export/solutionDemoExportConfig';
import { waitForExportCards } from './export/exportCaptureUtils';
import { useLeadershipExport } from './export/useLeadershipExport';
import {
  buildStableFilterOptions,
  extractChampionOptions,
  extractCohortOptions,
} from './bootcampFilterOptionsMapper';
import {
  fetchLeadershipBootcampParticipants,
  fetchLeadershipBootcampStats,
} from '../../../../Redux/Constants/apiService';
import '../../../../Styles/BootcampStats.css';

const SUB_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'individual-status', label: 'Individual Status' },
];

const INITIAL_OVERVIEW_FILTERS = {
  bootcampName: 'All',
  region: 'All',
  org: 'All',
  certificationStatus: 'All',
  cohort: 'All',
};

const INITIAL_INDIVIDUAL_FILTERS = {
  search: '',
  region: 'All',
  org: 'All',
  cohort: 'All',
  attendance: 'All',
  certificationStatus: 'All',
  champion: 'All',
};

const DEFAULT_OVERVIEW_DATA = {
  kpis: {
    totalParticipants: 0,
    certified: 0,
    completionRate: 0,
    pendingManagerReview: 0,
    missingCapstone: 0,
    pendingCapstoneReview: 0,
    incomplete: 0,
    registered: 0,
    failed: 0,
    noShow: 0,
    attendanceRate: 0,
    companiesCovered: 0,
    countriesCovered: 0,
  },
  certificationOutcomesByCohort: [],
  organizationBreakdown: [],
  certificationRateByRegion: [],
  certificationTrendsByCohort: [],
  topCountries: [],
  countryCertificationDetail: [],
  topCompanies: [],
  companyCertificationScorecard: [],
  championFollowUpWorkload: [],
  participantsRequiringFollowUp: [],
};

const DEFAULT_PARTICIPANTS_DATA = {
  statusSummary: {
    total: 0,
    completed: 0,
    incomplete: 0,
    pendingManagerReview: 0,
    missingCapstone: 0,
    pendingCapstoneReview: 0,
    registered: 0,
    failed: 0,
    noShow: 0,
    requiresFollowup: 0,
  },
  records: [],
  meta: {
    page: 1,
    pageSize: 15,
    totalItems: 0,
    totalPages: 1,
  },
};

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const toText = (value) => (value == null ? '' : String(value).trim());

const mapOverviewData = (bootcampStats = {}) => {
  const kpis = bootcampStats?.kpis || {};
  const outcomes = Array.isArray(bootcampStats?.certificationOutcomesByCohort)
    ? bootcampStats.certificationOutcomesByCohort
    : [];
  const derivedPendingReview = outcomes.reduce(
    (sum, item) =>
      sum +
      toNumber(
        item?.pendingManagerReview ?? item?.pending_mgr_review ?? item?.pendingMgrReview
      ),
    0
  );
  const derivedIncomplete = outcomes.reduce((sum, item) => sum + toNumber(item?.incomplete), 0);

  return {
    kpis: {
      totalParticipants: toNumber(kpis.totalParticipants),
      certified: toNumber(kpis.certified),
      completionRate: toNumber(kpis.completionRate),
      pendingManagerReview: toNumber(kpis.pendingManagerReview) || derivedPendingReview,
      pendingCapstoneReview: toNumber(kpis.pendingCapstoneReview),
      missingCapstone: toNumber(kpis.missingCapstone),
      incomplete: toNumber(kpis.incomplete) || derivedIncomplete,
      registered: toNumber(kpis.registered),
      failed: toNumber(kpis.failed),
      noShow: toNumber(kpis.noShow),
      attendanceRate: toNumber(kpis.attendanceRate),
      companiesCovered: toNumber(kpis.companiesCovered),
      countriesCovered: toNumber(kpis.countriesCovered),
    },
    certificationOutcomesByCohort: outcomes.map((item) => ({
      cohort: toText(item?.cohort ?? item?.cohortLabel),
      Completed: toNumber(item?.completed),
      'Missing Capstone': toNumber(item?.missingCapstone ?? item?.missing_capstone),
      'Pending Capstone Review': toNumber(
        item?.pendingCapstoneReview ?? item?.pending_capstone_review
      ),
      Registered: toNumber(item?.registered),
      Failed: toNumber(item?.failed),
      'No Show': toNumber(item?.noShow ?? item?.no_show),
    })),
    organizationBreakdown: (bootcampStats?.organizationBreakdown || []).map((item) => ({
      org: toText(item?.org),
      totalParticipants: toNumber(item?.totalParticipants ?? item?.total),
      certified: toNumber(item?.completed),
      pendingAction: toNumber(item?.pendingAction ?? item?.pending),
      completionRate: toNumber(item?.completionRate),
    })),
    certificationRateByRegion: (bootcampStats?.certificationRateByRegion || []).map((item) => ({
      region: toText(item?.region),
      certified: toNumber(item?.certified),
      total: toNumber(item?.total),
      completionRate: toNumber(item?.completionRate),
    })),
    certificationTrendsByCohort: (bootcampStats?.certificationTrendsByCohort || []).map((item) => ({
      cohort: toText(item?.cohort ?? item?.cohortLabel),
      completed: toNumber(item?.certified ?? item?.completed),
      total: toNumber(item?.totalParticipants ?? item?.total),
      completionRate: toNumber(item?.completionRate),
    })),
    topCountries: (bootcampStats?.topCountries || []).map((item) => ({
      country: toText(item?.country),
      region: toText(item?.region || 'NA'),
      certified: toNumber(item?.certified),
      total: toNumber(item?.total),
    })),
    countryCertificationDetail: (bootcampStats?.countryCertificationDetail || []).map((item) => ({
      country: toText(item?.country),
      region: toText(item?.region || 'NA'),
      certified: toNumber(item?.completed),
      pendingManagerReview: toNumber(
        item?.pendingManagerReview ?? item?.pending_mgr_review ?? item?.pendingMgrReview
      ),
      missingCapstone: toNumber(item?.missingCapstone ?? item?.missing_capstone),
      pendingCapstoneReview: toNumber(
        item?.pendingCapstoneReview ?? item?.pending_capstone_review
      ),
      incomplete: toNumber(item?.incomplete),
      failedNoShow: toNumber(item?.failedNoShow ?? item?.failed_no_show),
    })),
    topCompanies: (bootcampStats?.topCompanies || []).map((item) => ({
      company: toText(item?.company),
      org: toText(item?.org),
      certified: toNumber(item?.certified),
      totalParticipants: toNumber(item?.totalParticipants ?? item?.total),
    })),
    companyCertificationScorecard: (bootcampStats?.companyCertificationScorecard || []).map((item) => ({
      company: toText(item?.company),
      org: toText(item?.org),
      participants: toNumber(item?.participants ?? item?.totalParticipants ?? item?.total),
      certified: toNumber(item?.completed),
      pendingAction: toNumber(item?.pendingAction ?? item?.pending),
      completionRate: toNumber(item?.completionRate),
    })),
    championFollowUpWorkload: (bootcampStats?.championFollowUpWorkload || []).map((item) => ({
      champion: toText(item?.champion),
      participants: toNumber(item?.participants ?? item?.totalParticipants ?? item?.total),
      completed: toNumber(item?.completed),
      pendingAction: toNumber(item?.pendingAction ?? item?.pending),
      completionRate: toNumber(item?.completionRate),
    })),
    participantsRequiringFollowUp: (bootcampStats?.participantsRequiringFollowUp || []).map((item, index) => ({
      id: toText(item?.id) || `follow-up-${index}`,
      fullName: toText(item?.fullName),
      emailAddress: toText(item?.emailAddress),
      company: toText(item?.company),
      org: toText(item?.org),
      region: toText(item?.region || 'NA'),
      country: toText(item?.country),
      bootcampDate: toText(item?.cohortLabel),
      bootcampName: toText(item?.bootcampName),
      bmcHelixChampion: toText(item?.bmcHelixChampion ?? item?.champion),
      attendance: toText(item?.attendance),
      certification: toText(item?.certification),
      requiresFollowup: Boolean(item?.requiresFollowup),
    })),
  };
};

const mapParticipantsData = (participants = {}, fallbackSummary = {}) => {
  const statusSummary = participants?.statusSummary || {};
  const records = Array.isArray(participants?.records) ? participants.records : [];
  const meta = participants?.meta || {};

  return {
    statusSummary: {
      total: toNumber(statusSummary.total),
      completed: toNumber(statusSummary.completed),
      incomplete: toNumber(statusSummary.incomplete || fallbackSummary.incomplete),
      pendingManagerReview: toNumber(
        statusSummary.pendingManagerReview || fallbackSummary.pendingManagerReview
      ),
      pendingCapstoneReview: toNumber(statusSummary.pendingCapstoneReview),
      missingCapstone: toNumber(statusSummary.missingCapstone),
      registered: toNumber(statusSummary.registered),
      failed: toNumber(statusSummary.failed),
      noShow: toNumber(statusSummary.noShow),
      requiresFollowup: toNumber(statusSummary.requiresFollowup),
    },
    records: records.map((item, index) => ({
      id: toText(item?.id) || `participant-${index}`,
      fullName: toText(item?.fullName),
      emailAddress: toText(item?.emailAddress),
      company: toText(item?.company),
      org: toText(item?.org),
      region: toText(item?.region || 'NA'),
      country: toText(item?.country),
      cohort: toText(item?.cohortLabel),
      bootcampName: toText(item?.bootcampName),
      attendance: toText(item?.attendance),
      bmcHelixChampion: toText(item?.bmcHelixChampion),
      certification: toText(item?.certification),
      requiresFollowup: Boolean(item?.requiresFollowup),
    })),
    meta: {
      page: toNumber(meta.page) || 1,
      pageSize: toNumber(meta.pageSize) || 15,
      totalItems: toNumber(meta.totalItems),
      totalPages: toNumber(meta.totalPages) || 1,
    },
  };
};

const BootcampStatsSection = () => {
  const accessToken = useSelector((state) => state.auth?.accessToken);
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [overviewFilters, setOverviewFilters] = useState(INITIAL_OVERVIEW_FILTERS);
  const [individualFilters, setIndividualFilters] = useState(INITIAL_INDIVIDUAL_FILTERS);
  const [participantsPage, setParticipantsPage] = useState(1);
  const [overviewInitialLoading, setOverviewInitialLoading] = useState(true);
  const [overviewRefreshing, setOverviewRefreshing] = useState(false);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [overviewError, setOverviewError] = useState('');
  const [participantsError, setParticipantsError] = useState('');
  const [overviewData, setOverviewData] = useState(DEFAULT_OVERVIEW_DATA);
  const [participantsData, setParticipantsData] = useState(DEFAULT_PARTICIPANTS_DATA);
  const [cohortOptions, setCohortOptions] = useState([]);
  const [championOptions, setChampionOptions] = useState([]);
  const hasLoadedOverviewRef = useRef(false);
  const loadedOverviewKeyRef = useRef(null);
  const loadedParticipantsKeyRef = useRef(null);
  const previousIndividualFiltersKeyRef = useRef(null);
  const previousSubTabRef = useRef('overview');

  const {
    isExportDrawerOpen,
    setIsExportDrawerOpen,
    isExporting,
    selectedCount,
    toggleItem,
    selectAll,
    clearAll,
    isSelected,
    handleExport,
  } = useLeadershipExport({
    exportConfig: SOLUTION_DEMO_EXPORT_CONFIG,
    filterValues: overviewFilters,
    disabled: overviewInitialLoading,
    onBeforeExport: async (selectedItems) => {
      previousSubTabRef.current = activeSubTab;
      if (activeSubTab !== 'overview') {
        setActiveSubTab('overview');
        await waitForExportCards(selectedItems);
      }
    },
    onAfterExport: async () => {
      if (previousSubTabRef.current !== 'overview') {
        setActiveSubTab(previousSubTabRef.current);
      }
    },
  });

  const filterOptions = useMemo(
    () => buildStableFilterOptions({ cohorts: cohortOptions, champions: championOptions }),
    [cohortOptions, championOptions]
  );

  const overviewApiFilters = useMemo(
    () => ({
      bootcampName: overviewFilters.bootcampName,
      region: overviewFilters.region,
      org: overviewFilters.org,
      certificationStatus: overviewFilters.certificationStatus,
      cohort: overviewFilters.cohort,
      attendance: 'All',
      champion: 'All',
      search: '',
    }),
    [
      overviewFilters.bootcampName,
      overviewFilters.certificationStatus,
      overviewFilters.cohort,
      overviewFilters.org,
      overviewFilters.region,
    ]
  );

  const overviewRequestKey = useMemo(
    () => JSON.stringify(overviewApiFilters),
    [overviewApiFilters]
  );

  const individualFiltersSnapshot = useMemo(
    () => ({
      search: individualFilters.search,
      region: individualFilters.region,
      org: individualFilters.org,
      cohort: individualFilters.cohort,
      attendance: individualFilters.attendance,
      certificationStatus: individualFilters.certificationStatus,
      champion: individualFilters.champion,
    }),
    [
      individualFilters.search,
      individualFilters.region,
      individualFilters.org,
      individualFilters.cohort,
      individualFilters.attendance,
      individualFilters.certificationStatus,
      individualFilters.champion,
    ]
  );

  const individualFiltersKey = useMemo(
    () => JSON.stringify(individualFiltersSnapshot),
    [individualFiltersSnapshot]
  );

  const effectiveParticipantsPage = useMemo(() => {
    if (previousIndividualFiltersKeyRef.current !== individualFiltersKey) {
      return 1;
    }
    return participantsPage;
  }, [individualFiltersKey, participantsPage]);

  const participantsRequestKey = useMemo(
    () =>
      JSON.stringify({
        ...individualFiltersSnapshot,
        page: effectiveParticipantsPage,
        pageSize: 15,
        bootcampName: 'All',
      }),
    [individualFiltersSnapshot, effectiveParticipantsPage]
  );

  useEffect(() => {
    if (previousIndividualFiltersKeyRef.current === individualFiltersKey) {
      return;
    }

    previousIndividualFiltersKeyRef.current = individualFiltersKey;
    if (participantsPage !== 1) {
      setParticipantsPage(1);
    }
  }, [individualFiltersKey, participantsPage]);

  useEffect(() => {
    let isMounted = true;
    const isInitialLoad = !hasLoadedOverviewRef.current;

    if (isInitialLoad) {
      setOverviewInitialLoading(true);
    } else {
      setOverviewRefreshing(true);
    }

    const loadOverview = async () => {
      if (isInitialLoad) {
        setOverviewError('');
      }

      if (!accessToken) {
        if (!isMounted) return;
        if (isInitialLoad) {
          setOverviewData(DEFAULT_OVERVIEW_DATA);
          setOverviewError('Unable to load Solution Demo Analytics. Please try again.');
        }
        setOverviewInitialLoading(false);
        setOverviewRefreshing(false);
        return;
      }

      try {
        const response = await fetchLeadershipBootcampStats(accessToken, overviewApiFilters);
        const stats = response?.bootcampStats || {};
        if (!isMounted) return;
        setOverviewData(mapOverviewData(stats));
        setCohortOptions(extractCohortOptions(stats));
        setOverviewError('');
        hasLoadedOverviewRef.current = true;
        loadedOverviewKeyRef.current = overviewRequestKey;
      } catch (_error) {
        if (!isMounted) return;
        if (isInitialLoad) {
          setOverviewData(DEFAULT_OVERVIEW_DATA);
        }
        setOverviewError('Unable to load Solution Demo Analytics. Please try again.');
      } finally {
        if (isMounted) {
          setOverviewInitialLoading(false);
          setOverviewRefreshing(false);
        }
      }
    };

    if (loadedOverviewKeyRef.current === overviewRequestKey && hasLoadedOverviewRef.current) {
      setOverviewInitialLoading(false);
      setOverviewRefreshing(false);
      return undefined;
    }

    loadOverview();
    return () => {
      isMounted = false;
    };
  }, [accessToken, overviewApiFilters, overviewRequestKey]);

  useEffect(() => {
    if (activeSubTab !== 'individual-status') {
      return undefined;
    }

    if (loadedParticipantsKeyRef.current === participantsRequestKey) {
      return undefined;
    }

    let isMounted = true;

    const loadParticipants = async () => {
      setParticipantsLoading(true);
      setParticipantsError('');

      if (!accessToken) {
        if (!isMounted) return;
        setParticipantsData(DEFAULT_PARTICIPANTS_DATA);
        setParticipantsError('Unable to load participant statuses. Please try again.');
        setParticipantsLoading(false);
        return;
      }

      try {
        const params = {
          page: effectiveParticipantsPage,
          pageSize: 15,
          search: individualFiltersSnapshot.search,
          bootcampName: 'All',
          region: individualFiltersSnapshot.region,
          org: individualFiltersSnapshot.org,
          cohort: individualFiltersSnapshot.cohort,
          attendance: individualFiltersSnapshot.attendance,
          certificationStatus: individualFiltersSnapshot.certificationStatus,
          champion: individualFiltersSnapshot.champion,
        };
        const response = await fetchLeadershipBootcampParticipants(accessToken, params);
        if (!isMounted) return;
        const participants = response?.participants || {};
        setParticipantsData(mapParticipantsData(participants));
        setChampionOptions(extractChampionOptions(participants));
        setParticipantsError('');
        loadedParticipantsKeyRef.current = participantsRequestKey;
      } catch (_error) {
        if (!isMounted) return;
        setParticipantsData(DEFAULT_PARTICIPANTS_DATA);
        setParticipantsError('Unable to load participant statuses. Please try again.');
      } finally {
        if (isMounted) setParticipantsLoading(false);
      }
    };

    loadParticipants();
    return () => {
      isMounted = false;
    };
  }, [
    accessToken,
    activeSubTab,
    participantsRequestKey,
    effectiveParticipantsPage,
    individualFiltersSnapshot,
  ]);

  return (
    <div className="bootcamp-stats-section">
      <div className="bootcamp-subtabs-toolbar">
        <div className="bootcamp-subtabs" role="tablist" aria-label="Solution Demo Analytics subtabs">
          {SUB_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeSubTab === tab.id}
              className={`bootcamp-subtab ${activeSubTab === tab.id ? 'bootcamp-subtab-active' : ''}`}
              onClick={() => setActiveSubTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="leadership-export-trigger-btn"
          onClick={() => setIsExportDrawerOpen(true)}
          disabled={overviewInitialLoading || isExporting}
        >
          Export
        </button>
      </div>

      <BootcampContextualFilters
        activeSubTab={activeSubTab}
        overviewFilters={overviewFilters}
        onOverviewFiltersChange={setOverviewFilters}
        individualFilters={individualFilters}
        onIndividualFiltersChange={setIndividualFilters}
        filterOptions={filterOptions}
      />

      {activeSubTab === 'overview' && (
        <BootcampOverviewTab
          overviewData={overviewData}
          isInitialLoading={overviewInitialLoading}
          isRefreshing={overviewRefreshing}
          errorMessage={overviewError}
          onViewAllParticipantStatuses={() => setActiveSubTab('individual-status')}
        />
      )}

      {activeSubTab === 'individual-status' && (
        <BootcampIndividualStatusTab
          statusSummary={participantsData.statusSummary}
          rows={participantsData.records}
          paginationMeta={participantsData.meta}
          page={participantsPage}
          onPageChange={setParticipantsPage}
          isLoading={participantsLoading}
          errorMessage={participantsError}
        />
      )}

      <LeadershipExportDrawer
        isOpen={isExportDrawerOpen}
        onClose={() => {
          if (!isExporting) {
            setIsExportDrawerOpen(false);
          }
        }}
        exportConfig={SOLUTION_DEMO_EXPORT_CONFIG}
        selectedCount={selectedCount}
        isSelected={isSelected}
        onToggleItem={toggleItem}
        onSelectAll={selectAll}
        onClearAll={clearAll}
        onExport={handleExport}
        isExporting={isExporting}
      />
    </div>
  );
};

export default BootcampStatsSection;
